import {Mesh, MeshCallbacks, MeshMessageType} from '../../../mesh.js';
import {MessageReceiver} from '../../../common/channel.js';
import {randomId} from '../../../common/ids.js';
import {WebsocketConnection} from '../../../common/ws/types.js';
import {websocketConnectionFactory} from '../../../common/ws/factory.js';
import {getLogger} from '../../../logger.js';
import {Source} from '../../../state/types/common.js';
import {DomainInfo} from '../../../state/types/domain-registry.js';
import * as compat from '../../compat.js';

type Register = {
    type: 'register',
    nodeId: string,
    users?: string[],
    domains?: Array<DomainInfo>,
    callbacks?: MeshCallbacks
};
type Unregister = { type: 'unregister', nodeId: string, users?: string[] };
type Publish = { type: 'publish', nodeId: string, message: MeshMessageType };

// action to be taken (may contain non serializable data)
type Action
    = Register
    | Unregister
    | Publish;


type Data = { type: 'data', from: string, to: 'all' | string, message: compat.TransportMessage };
type NodeAdded = { type: 'node-added', 'node-id': string, 'new-node': string };
type NodeRemoved = { type: 'node-removed', 'node-id': string, 'removed-node': string };
type Command =
    { type: 'hello', 'node-id': string, domains}
    | { type: 'bye', 'node-id': string }
    | Data
    // received
    | NodeAdded
    | NodeRemoved
    ;

type Event = { type: 'connected' } | { type: 'disconnected' };

type Message = Event | Command | Action;

const logger = getLogger('gateway.mesh.ws.broker.client');

function hasNodeAddress(source?: Source): source is Source & { node: string } {
    return source !== undefined && 'node' in source && source.node !== undefined;
}

function publishNodePeer(connection: WebsocketConnection<Command>, nodeId: string, msg: compat.TransportMessage): void {
    const {receiver, body, source} = msg;
    if (hasNodeAddress(receiver)) {
        connection.send({type: 'data', from: nodeId, to: receiver.node, message: msg});
    } else {
        logger.error(`Destination nodeId is missing when sending message ${JSON.stringify(msg)}`);
    }

}

function publish(connection: WebsocketConnection<Command>, nodeId: string, msg: MeshMessageType) {
    const [receiverType, transportMessage] = compat.toTransportMessage(msg);
    switch (receiverType) {
        case 'cluster': {
            connection.send({type: 'data', from: nodeId, to: 'all', message: transportMessage});
            break;
        }
        case 'node': {
            publishNodePeer(connection, nodeId, transportMessage)
            break;
        }
        case 'peer': {
            publishNodePeer(connection, nodeId, transportMessage);
            break;
        }
        default: {
            logger.error(`Unhandled receiver type when publishing ${JSON.stringify(msg)}`);
            break;
        }
    }
}

class WebsocketBrokerMesh implements Mesh {
    constructor(private readonly ch: MessageReceiver<Action>) {
    }

    close() {
        this.ch(undefined as unknown as Action);// close it
    }

    register(nodeId: string | undefined, domains: Array<DomainInfo>, callbacks?: MeshCallbacks): string {
        if (nodeId === 'all') {
            throw new Error(`cannot register with node id 'all'`);
        }
        nodeId = nodeId ?? randomId();
        this.ch({type: 'register', nodeId, domains, callbacks});
        return nodeId;
    }

    unregister(nodeId: string) {
        this.ch({type: 'unregister', nodeId});
    }

    publish(nodeId: string, message: MeshMessageType) {
        this.ch({type: 'publish', nodeId, message});
    }

    addUsers(nodeId: string, added: string[]) {
        // do nothing
    }

    removeUsers(nodeId: string, removed: string[]) {
        // do nothing
    }
}

type NodeData = {callbacks?: MeshCallbacks, domains?: Array<DomainInfo>};

function handleRegister(msg: Register, nodes: Map<string, NodeData>, connection: WebsocketConnection<Command>) {
    const {nodeId, domains, callbacks} = msg;
    nodes.set(nodeId, {domains, callbacks});
    connection.send({type: 'hello', "node-id": nodeId, domains});
}

function handleUnregister(msg: Unregister, nodes: Map<string, NodeData>, connection: WebsocketConnection<Command>) {
    const nodeId = msg.nodeId;
    nodes.delete(nodeId);
    connection.send({type: 'bye', "node-id": nodeId});
}

function handlePublish(msg: Publish, connection: WebsocketConnection<Command>) {
    const nodeId = msg.nodeId;
    publish(connection, nodeId, msg.message);
}

function handleConnected(nodes: Map<string, NodeData>, connection: WebsocketConnection<Command>) {
    logger.info(`connected to mesh broker`);
    for (const [nodeId, {domains}] of nodes) {
        connection.send({type: 'hello', "node-id": nodeId, domains});
    }
}

function handleDisconnected(remote: Set<string>, nodes: Map<string, NodeData>) {
    logger.info(`disconnected from mesh broker`);

    const removed = new Set<string>(remote);
    remote.clear();

    for (const removedId of removed) {
        for (const [nodeId, {callbacks}] of nodes) {
            const cb = callbacks?.onMemberRemoved;
            if (cb) {
                cb(nodeId, removedId);
            }
        }
    }
}

function handleNodeAdded(msg: NodeAdded, remote: Set<string>, nodes: Map<string, NodeData>) {
    const {'node-id': nodeId, 'new-node': newNodeId} = msg;
    remote.add(newNodeId);
    const cb = nodes.get(nodeId)?.callbacks?.onMemberAdded;
    if (cb) {
        logger.info(`on-msg ${nodeId}, ${newNodeId}`);
        cb(nodeId, newNodeId);
    }
}

function handleNodeRemoved(msg: NodeRemoved, remote: Set<string>, nodes: Map<string, NodeData>) {
    const {'node-id': nodeId, 'removed-node': removedNodeId} = msg;
    remote.delete(removedNodeId);
    const cb = nodes.get(nodeId)?.callbacks?.onMemberRemoved;
    if (cb) {
        cb(nodeId, removedNodeId);
    }
}

function handleData(msg: Data, nodes: Map<string, NodeData>) {
    const {to: dest, from} = msg;
    for (const [nodeId, {callbacks}] of nodes) {
        if (nodeId !== from) {
            const cb = callbacks?.onMessage;
            if (cb) {
                cb(nodeId, compat.fromTransportMessage(msg.message));
            }
        }
    }
}

function handle(msg: Message, connection: WebsocketConnection<Command>, nodes: Map<string, NodeData>, remote: Set<string>) {
    switch (msg.type) {
        case 'register': {
            handleRegister(msg, nodes, connection);
            break;
        }
        case 'unregister': {
            handleUnregister(msg, nodes, connection);
            break;
        }
        case 'publish': {
            handlePublish(msg, connection);
            break;
        }
        case 'connected': {
            handleConnected(nodes, connection);
            break;
        }
        case 'disconnected': {
            handleDisconnected(remote, nodes);
            break;
        }
        case 'node-added': {
            handleNodeAdded(msg, remote, nodes);
            break;
        }
        case 'node-removed': {
            handleNodeRemoved(msg, remote, nodes);
            break;
        }
        case 'data': {
            handleData(msg, nodes);
            break;
        }
    }
}

export function websocketBrokerMesh(endpoint: string, websocket?: typeof WebSocket) {

    const nodes = new Map<string, { callbacks?: MeshCallbacks, domains: Array<DomainInfo> }>();
    const remote = new Set<string>();

    const connectionFactory = websocketConnectionFactory<Command>({codec: compat.codec<Command>(), websocket});
    const connection = connectionFactory.create(endpoint);


    const handler = (msg: Message) => {
        if (msg) {
            if (logger.enabledFor('debug')) {
                logger.debug(`mesh node processing command ${JSON.stringify(msg, null, '\t')}`);
            }
            handle(msg, connection, nodes, remote);
        }
    }

    connection.onConnect = () => handler({type: 'connected'});
    connection.onReceive = (msg) => handler(msg);
    // connection.onError = (err) => ;
    connection.onDisconnect = () => handler({type: 'disconnected'});

    connection.connect();
    return new WebsocketBrokerMesh(handler);
}
